import {
  differenceInDays,
  endOfDay,
  format,
  isBefore,
  isSameDay,
} from 'date-fns';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Flag } from 'shared/assets/icons';
import {
  DatepickerWithOptions,
  DatePresets,
  IconOptions,
} from 'shared/components/ui/datepicker-with-options';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  TaskMetaInteraction,
} from 'shared/components/ui/task-meta-interaction';
import { useToday } from 'shared/contexts/today';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { InteractionType } from 'shared/types/data-type';
import { DateFormatOptions } from 'shared/types/date-format-options';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';

import * as Styled from './task-meta-interaction-deadline-deadline.style';

const datePresets = [
  DatePresets.Today,
  DatePresets.Tomorrow,
  DatePresets.Friday,
];

export type TaskMetaInteractionDeadlineDeadlineProps = Omit<
  React.ComponentProps<typeof TaskMetaInteraction>,
  'start' | 'onClick' | 'label' | 'selected' | 'tooltipLabel'
> & {
  value?: Timestamp;
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  onChange: (date?: Timestamp) => void;
  highlight?: boolean;
};

export const TaskMetaInteractionDeadlineDeadline: React.FC<
  TaskMetaInteractionDeadlineDeadlineProps
> = ({
  value,
  dateFormat,
  weekStartsOn,
  onChange,
  highlight,

  ...rest
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();
  const today = useToday();

  const { due, overdue } = useMemo(() => {
    if (!value) {
      return { due: undefined, overdue: false };
    }

    if (isSameDay(value, today)) {
      return { due: t('general.labels.dates.today'), overdue: false };
    }

    if (isBefore(value, today)) {
      return {
        due: t('general.labels.days-ago', {
          count: differenceInDays(endOfDay(today), endOfDay(value)),
        }),
        overdue: true,
      };
    }

    return { due: format(value, 'd MMM'), overdue: false };
  }, [value, today, t]);

  const _onChange = (date?: Timestamp) => {
    onChange(date);
    closeMenu();
  };

  return (
    <Styled.Container ref={containerRef}>
      <TaskMetaInteraction
        startColor={overdue ? Colors.Error : Colors.Inherit}
        start={<Icon icon={Flag} />}
        color={highlight && value ? Colors.TextPrimary : Colors.Inherit}
        onClick={openMenu}
        label={due}
        selected={menuOpen}
        tooltipLabel={t(
          due
            ? 'task.deadline.tooltip.edit.label'
            : 'task.deadline.tooltip.add.label',
        )}
        {...rest}
      />

      {menuOpen && (
        <DatepickerWithOptions
          referenceElement={containerRef}
          interactionType={InteractionType.Task}
          datePresets={datePresets}
          value={value}
          onChange={_onChange}
          dateFormat={dateFormat}
          weekStartsOn={weekStartsOn}
          icon={IconOptions.Calendar}
          onOutside={closeMenu}
          canRemoveValue
        />
      )}
    </Styled.Container>
  );
};
